import './SiteSidebar.scss';
import logo from '../../assets/spectrum-reach-logo-ltbkg.svg';
import React, { useEffect, useState } from 'react';
import { useLayoutContext } from '../../providers/LayoutProvider';
import { KiteIcon } from '@kite/react-kite';
import { graphql, useStaticQuery } from 'gatsby';
import { sortMenuItems } from '../../shared/sortMenuItems';
import { SearchBar } from '../Header/SearchBar/SearchBar';
import { NavMenuItem } from '../../models/NavMenuItem';
import {
    Sidebar,
    SidebarBody,
    SidebarHeader,
} from '../../components/Sidebar/Sidebar';
import { SmartLink } from '../../components/SmartLink/SmartLink';

export const SiteSidebar = (
    { 
        excludeSearchBool 
    } : { 
        excludeSearchBool?: boolean;
    }) => {
    const {
        searchOpen,
        setSearchOpen,
        sidebarExpanded,
        setSidebarExpanded,
        currentSubmenu,
        setCurrentSubmenu,
    } = useLayoutContext();
    const [nav, setNav] = useState<NavMenuItem[]>([]);
    const [logoUrl, setLogoUrl] = useState(`/`);
    const [lastExpanded, setLastExpanded] = useState<string>(``);

    const menuData = useStaticQuery(graphql`
        query {
            allMenuItems(filter: { menu_name: { in: ["main", "login-bar"] } }) {
                nodes {
                    title
                    description
                    id
                    url
                    weight
                    menu_name
                    childrenMenuItems {
                        id
                        title
                        weight
                        url
                        menu_name
                    }
                }
            }
        }
    `);

    const bottomMenuItems = menuData.allMenuItems.nodes
        .filter((item) => item.menu_name === `login-bar`)
        .sort(sortMenuItems);

    useEffect(() => {
        const rawMenuItems = [
            ...menuData.allMenuItems.nodes
                .filter((item) => item.menu_name === `main`)
                .sort(sortMenuItems),
        ];

        const rLogoIdx = rawMenuItems.findIndex(
            (rItem) => rItem && rItem.title === `Spectrum Reach Logo`
        );

        if (rLogoIdx > -1) {
            const [reachLogo] = rawMenuItems.splice(rLogoIdx, 1);
            setLogoUrl(reachLogo.url);
        }

        const parentItems = rawMenuItems
            .filter((item) => item.childrenMenuItems.length)
            .sort(sortMenuItems);

        const menuItems: NavMenuItem[] = parentItems.map((item) => ({
            name: item.title,
            expanded: false,
            items: [
                item.description && {
                    name: item.description,
                    url: item.url,
                },
                ...item.childrenMenuItems.map((cItem) => ({
                    name: cItem.title,
                    url: cItem.url,
                })),
            ],
        }));

        for (const item of menuItems) {
            const idx = rawMenuItems.findIndex(
                (rItem) => rItem && rItem.title === item.name
            );
            rawMenuItems.splice(idx, 1);
            for (const subItem of item.items.filter(
                (sItem) => sItem && sItem.name
            )) {
                const idx = rawMenuItems.findIndex(
                    (rItem) => rItem && rItem.title === subItem.name
                );
                if (idx === -1) continue;
                rawMenuItems.splice(
                    rawMenuItems.findIndex(
                        (rItem) => rItem && rItem.title === subItem.name
                    ),
                    1
                );
            }
        }

        for (const item of rawMenuItems) {
            menuItems.push({
                name: item.title,
                url: item.url,
            });
        }
        menuItems.pop();

        setNav(menuItems);
    }, []);

    useEffect(() => {
        if (currentSubmenu) {
            setLastExpanded(currentSubmenu);
        }
    }, [currentSubmenu]);

    return (
        <>
            <Sidebar className="site-sidebar" expanded={sidebarExpanded}>
                <SidebarHeader>
                    <div className="site-sidebar__controls" >
                        {!currentSubmenu && (
                            <SmartLink className="logo" href={logoUrl} onClick={()=> {setSidebarExpanded(false)}}>
                                <img
                                    className="logo"
                                    src={logo}
                                    alt="Spectrum Reach logo"
                                />
                            </SmartLink>
                        )}
                        {currentSubmenu && (
                            <button
                                className="link-button"
                                onClick={() => setCurrentSubmenu(``)}
                                aria-label={`Close navigation submenu for ${currentSubmenu}`}
                            >
                                <KiteIcon icon="ki-chevron-left" />
                                {currentSubmenu}
                            </button>
                        )}
                        <div className="right-btns">
                            <button
                                onClick={() => {
                                    setSidebarExpanded(false);
                                    setSearchOpen(false);
                                }}
                                aria-label="Close mobile navigation"
                            >
                                <KiteIcon icon="ki-x" size="1.5rem" />
                            </button>
                        </div>
                    </div>
                    {searchOpen && !excludeSearchBool && <SearchBar mobileView={true}/>}
                </SidebarHeader>
                <SidebarBody>
                    <nav>
                        <ul
                            className={`main-nav ${
                                currentSubmenu ? `expanded` : ``
                            }`}
                        >
                            {nav.map((item) => (
                                <li key={item.name}>
                                    {item.items && item.items.length > 0 && (
                                        <>
                                            <button
                                                className="link-button hoverable"
                                                onClick={() =>
                                                    setCurrentSubmenu(item.name)
                                                }
                                                aria-expanded={
                                                    currentSubmenu === item.name
                                                }
                                                aria-label={`Show navigation submenu for ${item.name}`}
                                            >
                                                <div className="kite-container">
                                                    {item.name}
                                                    <KiteIcon icon="ki-chevron-right" />
                                                </div>
                                            </button>
                                            <ul
                                                onFocus={() => {
                                                    setCurrentSubmenu(
                                                        item.name
                                                    );
                                                }}
                                                onBlur={() => {
                                                    setCurrentSubmenu(``);
                                                }}
                                                className={`${
                                                    currentSubmenu === item.name
                                                        ? `expanded`
                                                        : ``
                                                } ${
                                                    lastExpanded === item.name
                                                        ? `was-expanded`
                                                        : ``
                                                }
                                                `.trim()}
                                            >
                                                {item.items
                                                    ?.filter(
                                                        (sItem) =>
                                                            sItem && sItem.name
                                                    )
                                                    .map((sItem) => (
                                                        <li key={sItem.name}>
                                                            <a
                                                                href={sItem.url}
                                                                className="link-button hoverable small"
                                                            >
                                                                <div className="kite-container">
                                                                    {sItem.name}
                                                                </div>
                                                            </a>
                                                        </li>
                                                    ))}
                                            </ul>
                                        </>
                                    )}
                                    {(!item.items ||
                                        item.items.length === 0) && (
                                        <a
                                            href={item.url}
                                            className="link-button hoverable"
                                        >
                                            <div className="kite-container">
                                                {item.name}
                                                <KiteIcon icon="ki-chevron-right" />
                                            </div>
                                        </a>
                                    )}
                                </li>
                            ))}
                        </ul>
                        <ul className="bottom">
                            <li className="kite-container">
                                <a href="/contact">Contact Us</a>
                            </li>
                            <li className="kite-container">
                                <a href="tel:+1-844-To-Reach">
                                    <KiteIcon icon="ki-phone" />
                                    1-844-To-Reach
                                </a>
                            </li>
                            {bottomMenuItems.map((item) => (
                                <li className="kite-container" key={item.id}>
                                    <SmartLink href={item.url}>
                                        {item.title}
                                    </SmartLink>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </SidebarBody>
            </Sidebar>
        </>
    );
};
